<template>
  <div>
    <v-dialog v-model="clearDialog" max-width="450" persistent>
      <v-card>
        <v-card-title>{{ $t('clearTasksTitle') }}</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense text>
            {{ $t('clearTasksWarning') }}
          </v-alert>
          <div class="mb-2">{{ $t('clearTasksGroups') }}</div>
          <v-checkbox v-model="clearScope.queue" :label="$t('queue')" hide-details dense />
          <v-checkbox v-model="clearScope.running" :label="$t('running')" hide-details dense />
          <v-checkbox
            v-model="clearScope.active"
            :label="$t('activeByProject')"
            hide-details
            dense
          />
          <v-checkbox v-model="clearScope.aliases" :label="$t('aliases')" hide-details dense />
          <v-checkbox v-model="clearScope.claims" :label="$t('claims')" hide-details dense />
          <v-checkbox
            v-model="clearScope.runtime_fields"
            :label="$t('runtimeFields')"
            hide-details
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="clearDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="error"
            :disabled="!isAnyGroupSelected || clearing"
            :loading="clearing"
            @click="clearTasks()"
          >
            {{ $t('clearTasksFromRedis') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-toolbar flat>
      <v-btn icon class="mr-4" @click="returnToProjects()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('clusterDashboard') }}</v-toolbar-title>
      <v-spacer />
      <v-btn
        color="error"
        @click="openClearDialog()"
        :disabled="!features.high_availability || !status.ha_enabled"
      >
        <v-icon left>mdi-delete-sweep</v-icon>
        {{ $t('clearTasksFromRedis') }}
      </v-btn>
    </v-toolbar>

    <v-divider />

    <v-alert v-if="!features.high_availability" text color="amber darken-3" class="PageAlert">
      <span class="mr-1" v-html="$t('ha_only_enterprise')"></span>

      <v-btn
        dark
        depressed
        v-if="isAdmin"
        color="amber darken-3"
        href="https://semaphoreui.com/enterprise?utm_source=app&utm_content=feature_cluster"
        target="_blank"
      >
        {{ $t('upgrade_to_pro') }}
      </v-btn>

      <span v-else style="font-weight: bold">
        {{ $t('contact_admin_to_upgrade_enterprise') }}
      </span>
    </v-alert>

    <v-alert v-if="status && !status.ha_enabled" type="info" dense text>
      {{ $t('haDisabledBanner') }}
    </v-alert>

    <div class="pa-4">
      <!-- Nodes -->
      <v-card v-if="status && status.nodes" class="mb-4" outlined>
        <v-card-title class="subtitle-1">{{ $t('nodes') }}</v-card-title>
        <v-data-table :headers="nodeHeaders" :items="status.nodes" :items-per-page="20" dense>
          <template v-slot:item.node_id="{ item }">
            <code>{{ item.node_id }}</code>
            <v-chip v-if="item.is_self" x-small class="ml-2">{{ $t('thisNode') }}</v-chip>
          </template>
          <template v-slot:item.alive="{ item }">
            <v-chip x-small :color="item.alive ? 'success' : 'error'" dark>
              {{ item.alive ? $t('alive') : $t('dead') }}
            </v-chip>
          </template>
          <template v-slot:item.last_heartbeat="{ item }">
            {{ formatTime(item.last_heartbeat) }}
          </template>
          <template v-slot:item.started_at="{ item }">
            {{ formatTime(item.started_at) }}
          </template>
        </v-data-table>
      </v-card>

      <!-- Redis -->
      <v-card v-if="status && status.redis" class="mb-4" outlined>
        <v-card-title class="subtitle-1">{{ $t('redisStatus') }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-row dense>
                <v-col>
                  <div class="text-caption grey--text">Addr</div>
                  <code>{{ status.redis.addr }}</code>
                </v-col>
                <v-col>
                  <div class="text-caption grey--text">{{ $t('connected') }}</div>
                  <v-chip x-small :color="status.redis.connected ? 'success' : 'error'" dark>
                    {{ status.redis.connected ? 'YES' : 'NO' }}
                  </v-chip>
                </v-col>
                <v-col>
                  <div class="text-caption grey--text">{{ $t('version') }}</div>
                  {{ status.redis.version || '—' }}
                </v-col>
                <v-col>
                  <div class="text-caption grey--text">{{ $t('usedMemory') }}</div>
                  {{ status.redis.used_memory || '—' }}
                </v-col>
                <v-col>
                  <div class="text-caption grey--text">{{ $t('totalKeys') }}</div>
                  {{ status.redis.total_keys }}
                </v-col>
              </v-row>
              <div class="text-caption grey--text mt-3">{{ $t('keyGroups') }}</div>
              <v-chip
                v-for="(count, group) in status.redis.key_groups || {}"
                :key="group"
                small
                class="mr-2 mt-1"
              >
                {{ group }}: {{ count }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">{{ $t('redisMemoryChart') }}</div>
              <RedisMemoryChart
                :history="memoryHistory"
                :window-ms="memoryWindowMs"
                :now-ts="memoryNow"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Task records -->
      <v-card outlined>
        <v-card-title class="subtitle-1">{{ $t('taskRecords') }}</v-card-title>
        <v-tabs v-model="tab">
          <v-tab>{{ $t('queue') }} ({{ queueRows.length }})</v-tab>
          <v-tab>{{ $t('running') }} ({{ runningRows.length }})</v-tab>
          <v-tab>{{ $t('activeByProject') }} ({{ activeRows.length }})</v-tab>
          <v-tab>{{ $t('aliases') }} ({{ aliasRows.length }})</v-tab>
          <v-tab>{{ $t('claims') }} ({{ claimRows.length }})</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-data-table :headers="taskHeaders" :items="queueRows" dense />
          </v-tab-item>
          <v-tab-item>
            <v-data-table :headers="taskHeaders" :items="runningRows" dense />
          </v-tab-item>
          <v-tab-item>
            <v-data-table :headers="taskHeaders" :items="activeRows" dense />
          </v-tab-item>
          <v-tab-item>
            <v-data-table :headers="aliasHeaders" :items="aliasRows" dense />
          </v-tab-item>
          <v-tab-item>
            <v-data-table :headers="claimHeaders" :items="claimRows" dense />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EventBus from '@/event-bus';
import RedisMemoryChart from '@/components/RedisMemoryChart.vue';

const MEMORY_WINDOW_MS = 60 * 60 * 1000;

export default {
  components: { RedisMemoryChart },

  props: {
    features: Object,
    isAdmin: Boolean,
  },

  data() {
    return {
      status: null,
      tasks: null,
      tab: 0,
      clearDialog: false,
      clearing: false,
      clearScope: {
        queue: true,
        running: false,
        active: false,
        aliases: false,
        claims: false,
        runtime_fields: false,
      },
      updateTimer: null,
      memoryHistory: [],
      memoryNow: Date.now(),
      memoryWindowMs: MEMORY_WINDOW_MS,
    };
  },

  computed: {
    isAnyGroupSelected() {
      return Object.values(this.clearScope).some((v) => v);
    },

    nodeHeaders() {
      return [
        { text: this.$t('nodeId'), value: 'node_id' },
        { text: this.$t('status'), value: 'alive' },
        { text: this.$t('lastHeartbeat'), value: 'last_heartbeat' },
        { text: this.$t('startedAt'), value: 'started_at' },
        { text: this.$t('version'), value: 'version' },
      ];
    },

    taskHeaders() {
      return [
        { text: 'Task', value: 'task_id' },
        { text: this.$t('project'), value: 'project_id' },
        { text: 'Template', value: 'template_id' },
        { text: this.$t('status'), value: 'status' },
        { text: 'Runner', value: 'runner_id' },
        { text: this.$t('username'), value: 'username' },
        { text: this.$t('owningNode'), value: 'node_id' },
      ];
    },

    aliasHeaders() {
      return [
        { text: this.$t('aliases'), value: 'alias' },
        { text: 'Task', value: 'task_id' },
      ];
    },

    claimHeaders() {
      return [{ text: 'Task', value: 'task_id' }];
    },

    queueRows() {
      return (this.tasks && this.tasks.queue) || [];
    },

    runningRows() {
      return (this.tasks && this.tasks.running) || [];
    },

    activeRows() {
      const byProj = (this.tasks && this.tasks.active_by_project) || {};
      return Object.values(byProj).reduce((acc, list) => acc.concat(list || []), []);
    },

    aliasRows() {
      const aliases = (this.tasks && this.tasks.aliases) || {};
      return Object.keys(aliases).map((alias) => ({ alias, task_id: aliases[alias] }));
    },

    claimRows() {
      return ((this.tasks && this.tasks.claims) || []).map((id) => ({ task_id: id }));
    },
  },

  created() {
    this.reload();
    this.updateTimer = setInterval(() => {
      if (!document.hidden) {
        this.reload();
      }
    }, 10000);
  },

  destroyed() {
    clearInterval(this.updateTimer);
  },

  methods: {
    async returnToProjects() {
      EventBus.$emit('i-open-last-project');
    },

    formatTime(value) {
      if (!value || value.startsWith('0001-01-01')) {
        return '—';
      }
      return new Date(value).toLocaleString();
    },

    async reload() {
      try {
        this.status = (
          await axios({
            method: 'get',
            url: '/api/cluster',
            responseType: 'json',
          })
        ).data;

        this.recordMemorySample();

        this.tasks = (
          await axios({
            method: 'get',
            url: '/api/cluster/tasks',
            responseType: 'json',
          })
        ).data;
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: `Failed to load cluster status: ${err.message}`,
        });
      }
    },

    recordMemorySample() {
      const now = Date.now();
      this.memoryNow = now;
      const redis = this.status && this.status.redis;
      if (!redis || !redis.connected) {
        return;
      }
      this.memoryHistory.push({
        time: now,
        bytes: redis.used_memory_bytes || 0,
      });
      const cutoff = now - this.memoryWindowMs;
      const firstInWindow = this.memoryHistory.findIndex((p) => p.time >= cutoff);
      if (firstInWindow > 0) {
        this.memoryHistory.splice(0, firstInWindow);
      }
    },

    openClearDialog() {
      this.clearScope = {
        queue: true,
        running: false,
        active: false,
        aliases: false,
        claims: false,
        runtime_fields: false,
      };
      this.clearDialog = true;
    },

    async clearTasks() {
      this.clearing = true;
      try {
        const res = (
          await axios({
            method: 'delete',
            url: '/api/cluster/tasks',
            responseType: 'json',
            data: { scope: this.clearScope },
          })
        ).data;

        EventBus.$emit('i-snackbar', {
          color: 'success',
          text: `Cleared ${res.deleted_keys} record(s) from the backend.`,
        });
        this.clearDialog = false;
        await this.reload();
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: `Failed to clear tasks: ${err.message}`,
        });
      } finally {
        this.clearing = false;
      }
    },
  },
};
</script>
